import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { publicLeadsApi, type LeadInterest } from '@/services/api';
import { LoadingSpinner } from '@/components/common';
import { LeadPublicShell } from '@/components/crm/LeadPublicShell';
import { leadInputClass, leadLabelClass } from '@/components/crm/styles';

const INTERESTS: { value: LeadInterest; label: string; hint: string }[] = [
  { value: 'productos', label: 'Información de productos', hint: 'Te enviamos el catálogo por correo.' },
  { value: 'proyecto', label: 'Impulsar un proyecto', hint: 'Te contactamos para conocer tu proyecto.' },
  { value: 'otro', label: 'Otro', hint: 'Cuéntanos qué necesitas.' },
];

const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo enviar. Prueba de nuevo.';

/** Formulario abierto de captación (ALTA LEAD 1.1). Ruta pública /solicitud/:orgSlug. */
export default function LeadRequestPage() {
  const { orgSlug = '' } = useParams();
  const [org, setOrg] = useState<{ name: string; logoUrl: string | null } | null>(null);
  const [missing, setMissing] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', interest: '' as LeadInterest | '', comment: '', fax: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicLeadsApi.info(orgSlug).then((r) => setOrg(r.data)).catch(() => setMissing(true));
  }, [orgSlug]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.interest) { setError('Elige qué te interesa'); return; }
    if (form.interest === 'otro' && !form.comment.trim()) { setError('Cuéntanos qué necesitas'); return; }
    setBusy(true);
    setError('');
    try {
      await publicLeadsApi.request(orgSlug, {
        email: form.email.trim(),
        name: form.name.trim() || undefined,
        interest: form.interest,
        comment: form.comment.trim() || undefined,
        fax: form.fax || undefined,
      });
      setDone(true);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  if (missing) return <LeadPublicShell org={null}><p className="text-gray-600">Este formulario no está disponible.</p></LeadPublicShell>;
  if (!org) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><LoadingSpinner /></div>;

  if (done) {
    return (
      <LeadPublicShell org={org}>
        <div className="flex flex-col items-center text-center gap-3 py-6">
          <CheckCircleIcon className="w-12 h-12 text-vida-600" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-gray-900">Gracias, hemos recibido tu solicitud</h1>
          <p className="text-gray-600">
            {form.interest === 'productos' ? 'En breve te llegará el catálogo a tu correo.' : 'Nos pondremos en contacto contigo.'}
          </p>
        </div>
      </LeadPublicShell>
    );
  }

  return (
    <LeadPublicShell org={org}>
      <h1 className="text-xl font-semibold text-gray-900">¿En qué podemos ayudarte?</h1>
      <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
        <div>
          <label htmlFor="lead-email" className={leadLabelClass}>Email</label>
          <input id="lead-email" type="email" required autoComplete="email" maxLength={200} className={leadInputClass}
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label htmlFor="lead-name" className={leadLabelClass}>Nombre <span className="text-gray-400 font-normal">(opcional)</span></label>
          <input id="lead-name" autoComplete="name" maxLength={200} className={leadInputClass}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <fieldset>
          <legend className={leadLabelClass}>Me interesa</legend>
          <div className="space-y-2">
            {INTERESTS.map((i) => (
              <label key={i.value} className={`flex gap-3 items-start rounded-lg border p-3 cursor-pointer ${form.interest === i.value ? 'border-vida-600 bg-vida-50' : 'border-gray-300 hover:border-vida-400'}`}>
                <input type="radio" name="interest" value={i.value} className="mt-1" checked={form.interest === i.value}
                  onChange={() => setForm({ ...form, interest: i.value })} />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{i.label}</span>
                  <span className="block text-xs text-gray-500">{i.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {(form.interest === 'otro' || form.interest === 'proyecto') && (
          <div>
            <label htmlFor="lead-comment" className={leadLabelClass}>
              {form.interest === 'otro' ? 'Cuéntanos qué necesitas' : 'Cuéntanos tu proyecto (opcional)'}
            </label>
            <textarea id="lead-comment" rows={4} maxLength={2000} className={leadInputClass}
              value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          </div>
        )}
        {/* Honeypot: oculto por CSS (no type="hidden"), fuera del orden de tabulación. */}
        <div className="absolute left-[-10000px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
          <label htmlFor="lead-fax">Fax</label>
          <input id="lead-fax" name="fax" tabIndex={-1} autoComplete="off" value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} />
        </div>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <button type="submit" disabled={busy}
          className="w-full py-3 bg-vida-700 text-white rounded-lg font-medium hover:bg-vida-800 disabled:opacity-50">
          {busy ? 'Enviando…' : 'Enviar'}
        </button>
        <p className="text-xs text-gray-500">Usaremos tus datos solo para responder a esta solicitud.</p>
      </form>
    </LeadPublicShell>
  );
}
